<div class="doc-collector">

    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="doc-collector__header">
        <div class="doc-collector__header-left">
            <mat-icon [svgIcon]="'document-card-icon'" class="doc-collector__header-icon"></mat-icon>
            <div class="doc-collector__header-meta">
                <span class="doc-collector__title">Documents Collected</span>
                <span class="doc-collector__subtitle">Document review and extracted field verification.</span>
            </div>
        </div>

        <div class="doc-collector__header-actions">
            @if (!isReadOnly && !isAllMarkedFinal) {
            <app-buttons [label]="'Upload'" [variant]="'secondary'" (clicked)="openUploadModal()">
                <mat-icon btn-icon-start [svgIcon]="'upload'"></mat-icon>
            </app-buttons>
            }

            <app-buttons [label]="'Download All'" [variant]="'secondary'" (clicked)="onDownloadAll()">
                <mat-icon btn-icon-start [svgIcon]="'icon-download'"></mat-icon>
            </app-buttons>

            @if (!isReadOnly) {
            <app-buttons [label]="isAllMarkedFinal ? 'Marked as Final - Reopen' : 'Mark All as Final'"
                [variant]="'primary'" [class.doc-collector__mark-final-btn--reopen]="isAllMarkedFinal"
                (clicked)="onMarkAllAsFinal()">
                <mat-icon btn-icon-start [svgIcon]="'icon-success-check'"></mat-icon>
            </app-buttons>
            }

            <button class="doc-collector__icon-btn doc-collector__icon-btn--square"
                [title]="isOpen ? 'Collapse' : 'Expand'" (click)="toggleSection()">
                <mat-icon [svgIcon]="isOpen ? 'minus' : 'plus'"></mat-icon>
            </button>

        </div>
    </div>

    @if (isOpen) {

    <!-- ── Loading shimmer ──────────────────────────────────── -->
    @if (loading) {
    <div class="doc-collector__loading">
        <span class="doc-collector__shimmer doc-collector__shimmer--row"></span>
        <span class="doc-collector__shimmer doc-collector__shimmer--row"></span>
        <span class="doc-collector__shimmer doc-collector__shimmer--row doc-collector__shimmer--short"></span>
    </div>
    }

    <!-- ── Error banner ─────────────────────────────────────── -->
    @if (!loading && error) {
    <div class="doc-collector__alert doc-collector__alert--error">
        <mat-icon [svgIcon]="'info-icon'" class="doc-collector__alert-icon"></mat-icon>
        <p class="doc-collector__alert-text">
            <strong class="doc-collector__alert-text--bold">Error:</strong>
            <span class="doc-collector__alert-text--light">{{ error }}</span>
        </p>
    </div>
    }

    <!-- ── Main content ─────────────────────────────────────── -->
    @if (!loading && !error) {

    @if (shouldShowActionRequiredBanner) {
    <div class="doc-collector__alert">
        <mat-icon [svgIcon]="'info-icon'" class="doc-collector__alert-icon"></mat-icon>
        <p class="doc-collector__alert-text">
            <strong class="doc-collector__alert-text--bold">Action Required:</strong>
            <span class="doc-collector__alert-text--light">
                Ensure all mandatory documents are uploaded and expand document rows to resolve data
                mismatches before marking as final.
            </span>
        </p>
    </div>
    }

    <!-- ══════════════════════════════════════════════════════
           SECTION 1 — Personal Documents
           ══════════════════════════════════════════════════════ -->
    <section class="doc-collector__section">
        <div class="doc-collector__section-header">
            <span class="doc-collector__section-title">1. Personal Documents</span>
            <span class="doc-collector__section-count">
                <span class="doc-collector__section-count-value"
                    [class.doc-collector__section-count-value--complete]="isSectionReady(primaryDocTypes)"
                    [class.doc-collector__section-count-value--error]="getUploadedCount(primaryDocTypes) === 0">
                    {{ getUploadedCount(primaryDocTypes) }}/{{ primaryDocTypes.length }}
                </span> Uploaded
            </span>
        </div>
        @for (docType of primaryDocTypes; track docType) {
        @if (getDocumentsByType(docType).length > 0) {
        @for (file of getDocumentsByType(docType); track file) {
        <ng-container
            *ngTemplateOutlet="docRow; context: { docType: docType, file: file, canonicalDocType: docType }"></ng-container>
        }
        } @else {
        <ng-container
            *ngTemplateOutlet="docRow; context: { docType: docType, file: null, canonicalDocType: docType }"></ng-container>
        }
        }
    </section>

    <!-- ══════════════════════════════════════════════════════
           SECTION 2 — Policy-Level Documents
           ══════════════════════════════════════════════════════ -->
    <section class="doc-collector__section">
        <div class="doc-collector__section-header">
            <span class="doc-collector__section-title">2. Policy-Level Documents</span>
            <span class="doc-collector__section-count">
                <span class="doc-collector__section-count-value"
                    [class.doc-collector__section-count-value--complete]="isSectionReady(policyDocTypes)"
                    [class.doc-collector__section-count-value--error]="getUploadedCount(policyDocTypes) === 0">
                    {{ getUploadedCount(policyDocTypes) }}/{{ policyDocTypes.length }}
                </span> Uploaded
            </span>
        </div>
        @for (docType of policyDocTypes; track docType) {
        @if (getDocumentsByType(docType).length > 0) {
        @for (file of getDocumentsByType(docType); track file) {
        <ng-container
            *ngTemplateOutlet="docRow; context: { docType: docType, file: file, canonicalDocType: docType }"></ng-container>
        }
        } @else {
        <ng-container
            *ngTemplateOutlet="docRow; context: { docType: docType, file: null, canonicalDocType: docType }"></ng-container>
        }
        }
    </section>

    <!-- ══════════════════════════════════════════════════════
           SECTION 3 — Sponsor/Company Documents
           Shown only when: primary applicant AND sponsor is Company
           (same guard as upload-center's !isSponsorSelf logic)
           ══════════════════════════════════════════════════════ -->
    @if (isPrimaryApplicant && isSponsorCompany && !isLeadSelfSponsored()) {
    <section class="doc-collector__section">
        <div class="doc-collector__section-header">
            <span class="doc-collector__section-title">3. Sponsor / Company Documents</span>
            <span class="doc-collector__section-count">
                <span class="doc-collector__section-count-value"
                    [class.doc-collector__section-count-value--complete]="isSectionReady(sponsorDocTypes, true)"
                    [class.doc-collector__section-count-value--error]="getSponsorUploadedCount(sponsorDocTypes) === 0">
                    {{ getSponsorUploadedCount(sponsorDocTypes) }}/{{ sponsorDocTypes.length }}
                </span> Uploaded
            </span>
        </div>

        @for (sponsor of sponsorshipDocuments; track sponsor) {
        @for (docType of sponsorDocTypes; track docType) {
        <ng-container *ngTemplateOutlet="docRow; context: {
            docType: docType,
            file: getSponsorDocument(sponsor, docType),
            isSponsor: true,
            canonicalDocType: docType
              }">
        </ng-container>
        }
        }
    </section>
    }

    <!-- ══════════════════════════════════════════════════════
           SECTION 4 — Additional Documents
           ══════════════════════════════════════════════════════ -->
    <section class="doc-collector__section">
        <div class="doc-collector__section-header">
            <span class="doc-collector__section-title">{{ getAdditionalDocumentsSectionNumber() }}. Additional Documents</span>
            <span class="doc-collector__section-count">
                <span class="doc-collector__section-count-value"
                    [class.doc-collector__section-count-value--complete]="isSectionReady(additionalDocTypes)"
                    [class.doc-collector__section-count-value--error]="getUploadedCount(additionalDocTypes) === 0">
                    {{ getUploadedCount(additionalDocTypes) }}/{{ additionalDocTypes.length }}
                </span> Uploaded
            </span>
        </div>
        @for (docType of additionalDocTypes; track docType) {
        @if (getDocumentsByType(docType).length > 0) {
        @for (file of getDocumentsByType(docType); track file) {
        <ng-container
            *ngTemplateOutlet="docRow; context: { docType: docType, file: file, canonicalDocType: docType }"></ng-container>
        }
        } @else {
        <ng-container
            *ngTemplateOutlet="docRow; context: { docType: docType, file: null, canonicalDocType: docType }"></ng-container>
        }
        }
    </section>

    }


    <!-- ══════════════════════════════════════════════════════════
       REUSABLE ROW TEMPLATE
       context: docType, file, isSponsor? (optional)
       ══════════════════════════════════════════════════════════ -->
    <ng-template #docRow let-docType="docType" let-file="file" let-isSponsor="isSponsor"
        let-canonicalDocType="canonicalDocType">

        <div class="doc-collector__row" [class.doc-collector__row--expanded]="isRowExpanded(file)"
            [class.doc-collector__row--deleting]="isRowDeleting(file)"
            [class.doc-collector__row--error]="shouldHighlightDocumentRow(file) || isMandatoryDocTypeMissing(docType)"
            [class.doc-collector__row--flash-match]="getDocumentFlashState(file) === 'match'"
            [class.doc-collector__row--flash-mismatch]="getDocumentFlashState(file) === 'mismatch'"
            [class.doc-collector__row--flash-corrected]="getDocumentFlashState(file) === 'corrected'"
            [class.doc-collector__row--flash-deleted]="getDocumentFlashState(file) === 'deleted'">
            <div class="doc-collector__row-header">
                <mat-icon [svgIcon]="'document-file-icon'" class="doc-collector__row-icon"></mat-icon>

                <!-- Info -->
                <div class="doc-collector__row-info">
                    <div class="doc-collector__row-name">
                        <app-tooltip [text]="file?.fileName || getDocLabel(docType)">
                            <span class="doc-collector__row-name doc-collector__row-name-text"
                                [class.doc-collector__row--pending]="!file">
                                @if (isMandatoryDocType(canonicalDocType || docType, !!isSponsor)) {
                                <span class="doc-collector__mandatory-asterisk">*</span>
                                }
                                {{ file?.fileName || getDocLabel(docType) }}
                            </span>
                        </app-tooltip>
                        @if (shouldShowDocumentInfoTooltip(file, canonicalDocType || docType)) {
                        <app-tooltip [text]="getDocTooltip(canonicalDocType || docType)!" [disabled]="false">
                            <mat-icon [svgIcon]="'icon-help-circle'"
                                class="doc-collector__row-name-icon doc-collector__row-name-icon--muted doc-collector__row-name-icon--tooltip">
                            </mat-icon>
                        </app-tooltip>
                        }
                        <!-- Green tick — uploaded -->
                        @if (file && getRowStatusIcon(file, canonicalDocType || docType) !== 'none') {
                        @if (getRowStatusIcon(file, canonicalDocType || docType) === 'success') {
                        <mat-icon [svgIcon]="'tick'"
                            class="doc-collector__row-name-icon doc-collector__row-name-icon--success">
                        </mat-icon>
                        } @else if (getRowStatusIcon(file, canonicalDocType || docType) === 'info') {
                        <mat-icon [svgIcon]="'tick'"
                            class="doc-collector__row-name-icon doc-collector__row-name-icon--info">
                        </mat-icon>
                        } @else {
                        <mat-icon [svgIcon]="'info-icon'"
                            class="doc-collector__row-name-icon doc-collector__row-name-icon--warning">
                        </mat-icon>
                        }
                        }
                    </div>


                    <span class="doc-collector__row-meta">
                        @if (file) {
                        <span class="doc-collector__row-meta-label">Source:</span>
                        <span class="doc-collector__row-meta-value">
                            @if (docType === 'Others' && file?.documentLabel) {
                            {{ file.documentLabel }} (Other Relevant Document)
                            } @else {
                            {{ getDocLabel(docType) }}
                            }
                        </span>
                        <span class="doc-collector__row-dot" aria-hidden="true"></span>
                        <span class="doc-collector__row-meta-value">{{ parseDate(file.date) | date:'d MMMM, yyyy'
                            }}</span>
                        <span class="doc-collector__row-dot" aria-hidden="true"></span>
                        <span class="doc-collector__row-meta-value">
                            {{ (file.fileSize / (1024 * 1024)).toFixed(2) }} MB
                        </span>
                        } @else {
                        <span class="doc-collector__row-meta-value doc-collector__row-meta--muted"
                            [class.doc-collector__row--pending]="!file">Not Uploaded</span>
                        }
                    </span>
                </div>

                <!-- Tags use it based on condition -->
                <div class="doc-collector__row-tags">
                    @if (file) {
                    @if (getDocumentBannerType(file, canonicalDocType || docType) === 'match') {
                    <span class="doc-collector__tag doc-collector__tag--info">
                        Extracted
                    </span>
                    }

                    @if (getDocumentBannerType(file, canonicalDocType || docType) === 'mismatch') {
                    <span class="doc-collector__tag doc-collector__tag--info">
                        Extracted
                    </span>
                    <span class="doc-collector__tag doc-collector__tag--warning">
                        <mat-icon [svgIcon]="'del-warning'" class="doc-collector__tag-icon"></mat-icon>
                        {{ getDocumentMismatchCount(file, canonicalDocType || docType) }} Mismatch
                    </span>
                    }

                    @if (getDocumentBannerType(file, canonicalDocType || docType) === 'failed') {
                    <app-tooltip [text]="getDocumentFailureTooltip(file)">
                        <span class="doc-collector__tag doc-collector__tag--danger">
                            <mat-icon [svgIcon]="'cross-in-circle-icon'" class="doc-collector__tag-icon"></mat-icon>
                            Extraction Failed
                        </span>
                    </app-tooltip>
                    @if (getDocumentMismatchCount(file, canonicalDocType || docType) > 0) {
                    <span class="doc-collector__tag doc-collector__tag--warning">
                        <mat-icon [svgIcon]="'del-warning'" class="doc-collector__tag-icon"></mat-icon>
                        {{ getDocumentMismatchCount(file, canonicalDocType || docType) }} Mismatch
                    </span>
                    }
                    }

                    }
                </div>

                <!-- Actions -->
                <div class="doc-collector__row-actions">
                    @if (file) {
                    <button class="doc-collector__action-btn" title="View" (click)="handleFileAction('view', file)">
                        <mat-icon [svgIcon]="'icon-eye'"></mat-icon>
                    </button>
                    <button class="doc-collector__action-btn" title="Download"
                        (click)="handleFileAction('download', file)">
                        <mat-icon [svgIcon]="'icon-download'"></mat-icon>
                    </button>
                    @if (!isReadOnly && !isAllMarkedFinal) {
                    <button class="doc-collector__action-btn doc-collector__action-btn--danger" title="Delete"
                        (click)="onDeleteFile(file)">
                        <mat-icon [svgIcon]="'trash'"></mat-icon>
                    </button>
                    }
                    } @else {
                    @if (!isReadOnly && !isAllMarkedFinal) {
                    <!-- Upload button triggers the hidden input scoped to this row -->
                    <button class="doc-collector__action-btn doc-collector__action-btn--upload" title="Upload"
                        (click)="triggerUpload(docType)">
                        <mat-icon [svgIcon]="'upload'"></mat-icon>
                    </button>
                    }
                    }
                </div>

                <!-- Passport pages only hide the expand control when there are no extracted rows to review. -->
                @if (file && isDocExtractable(canonicalDocType || docType) && (!isPassportDocument(canonicalDocType || docType) || canShowExtractionTable(file, canonicalDocType || docType))) {
                <app-tooltip [text]="isRowExpanded(file) ? 'Collapse to hide details' : 'Expand to verify details'">
                    <button class="doc-collector__expand-btn" (click)="toggleExpandRow(file)">
                        <mat-icon [svgIcon]="isRowExpanded(file) ? 'minus' : 'plus'"></mat-icon>
                    </button>
                </app-tooltip>
                }
            </div>
            @if (isRowExpanded(file)) {
            <div class="doc-collector__row-body">
                <div class="doc-collector__review-box">
                    <div class="doc-collector__review-hint">
                        <mat-icon [svgIcon]="'info-icon'" class="doc-collector__review-hint-icon"></mat-icon>
                        <span>{{ (isReadOnly || isAllMarkedFinal) ? 'Read-only mode: review extracted values.' :
                            extractedTableHintText }}</span>
                    </div>

                    <app-editable-review-table [rows]="getFieldRows(file, canonicalDocType || docType)"
                        [columns]="extractedFieldTableColumnsByMode" [showHint]="false"
                        [statusLabels]="tableStatusLabels"
                        [clickToEditTooltipText]="(isReadOnly || isAllMarkedFinal) ? '' : finalValueEditTooltipText"
                        [isReadOnly]="isReadOnly || isAllMarkedFinal" [overrideReasonOptions]="overrideReasonOptions"
                        [invalidOverrideReasonRowKeys]="getMissingOverrideReasonRowKeys(file)"
                        [invalidFinalValueRowKeys]="getMissingFinalValueRowKeys(file)"
                        [enableCopyMode]="enableCopyMode && isReadOnly"
                        (finalValueChanged)="onFinalValueChanged(file, $event.rowKey, $event.value)"
                        (overrideReasonChanged)="onOverrideReasonChanged(file, $event.rowKey, $event.reason)">
                    </app-editable-review-table>
                </div>
            </div>
            }

        </div>

    </ng-template>


    <!-- ── Delete Confirmation Modal (mirrors upload-center pattern) ── -->
    @if (showDeleteModal) {
    <app-popup-modal class="cancel-modal-popup" [width]="'400px'" [imageSrc]="images.Cancel_Logo"
        [buttons]="deleteModalButtons" [isCloseIcon]="false" (closePopupModal)="cancelDelete()">
        <div custom-content>
            <div class="delete-modal-title-below">{{ deleteModalTitle }}</div>
            <div class="delete-modal-content">
                <div *ngFor="let section of deleteModalSections" class="delete-modal-section"
                    [ngClass]="{'delete-modal-section--center': !section.icon}">
                    <mat-icon *ngIf="section.icon" [svgIcon]="section.icon" [ngClass]="section.iconClass"
                        class="delete-modal-icon"></mat-icon>
                    <div class="delete-modal-section-text">
                        <span *ngIf="section.label" class="delete-modal-label">{{ section.label }}</span>
                        <span class="delete-modal-text">{{ section.text }}</span>
                    </div>
                </div>
            </div>
        </div>
    </app-popup-modal>
    }

    <!-- ── Document Preview Modal ─────────────────────────────── -->
    @if (showPreviewModal && previewFile) {
    <app-document-preview-modal [title]="previewFile.fileName" [tags]="[getDocLabel(previewFile.documentType)]"
        [fileUrl]="previewFile.viewFilePath" (close)="closePreviewModal()">
    </app-document-preview-modal>
    }

    @if (showRequoteWarningModal) {
    <app-popup-modal [width]="'520px'" [imageSrc]="images.Cancel_Logo" [isCloseIcon]="true" [buttons]="requoteWarningButtons"
        (closePopupModal)="closeRequoteWarningModal()">
        <div custom-content class="doc-collector__requote-warning">
            <div class="doc-collector__requote-warning-title">Potential Requote Required</div>
            <div class="doc-collector__requote-warning-description">
                A mismatch has been detected in fields that affect premium calculation.
            </div>
            <div class="doc-collector__requote-warning-list">
                @for (row of requoteWarningRows; track row.documentId + '-' + row.rowKey) {
                <div class="doc-collector__requote-warning-item">
                    <div class="doc-collector__requote-warning-field">{{ row.fieldLabel }}</div>
                    <div class="doc-collector__requote-warning-values">
                        <span
                            class="doc-collector__requote-warning-value doc-collector__requote-warning-value--submitted">
                            Submitted: {{ row.submittedValue }}
                        </span>
                        <span class="doc-collector__requote-warning-arrow">&#8594;</span>
                        <span class="doc-collector__requote-warning-value doc-collector__requote-warning-value--final">
                            Extracted: {{ row.extractedValue }}
                        </span>
                    </div>
                </div>
                }
            </div>
            <div class="doc-collector__requote-warning-note">
                Please confirm how you wish to proceed.
            </div>
        </div>
    </app-popup-modal>
    }

    }

</div>